import type { RouteRecordRaw } from 'vue-router'
import { CAPABILITIES } from './nav'

/**
 * Analytics and the notification centre, for three workspaces.
 *
 * `analytics/:section?` — the analytics screen is four views of one report, and the open one
 * is in the URL so it can be linked, bookmarked and returned to by the back button. It is
 * **one route with one query**, not four: every view reads the same payload, so tabbing must
 * not refetch. The parameter is optional and an unknown value falls back to the first tab,
 * because a stale link should land somewhere rather than on an error.
 *
 * The workspace lives in `meta`, which is what the page reads to decide which API prefix to
 * call. It is never a query parameter: an administrator's numbers are reachable only from an
 * administrator's route, behind an administrator's permission class, and the server enforces
 * the same boundary again.
 *
 * Paths come from `docs/v2/route-map.md` and `affiliate-workspace.md` §4.
 */
const base = { layout: 'dashboard', requiresAuth: true } as const

export const insightsRoutes: RouteRecordRaw[] = [
  {
    path: '/app/affiliate/analytics/:section?',
    name: 'affiliate-analytics',
    component: () => import('./pages/AffiliateAnalyticsPage.vue'),
    meta: {
      ...base,
      workspace: 'affiliate' as const,
      viewParam: true,
      capability: CAPABILITIES.affiliateAnalytics,
      title: 'affiliate.nav.analytics',
    },
  },
  {
    path: '/app/affiliate/notifications',
    name: 'affiliate-notifications',
    component: () => import('~/modules/notifications/pages/NotificationsPage.vue'),
    meta: { ...base, workspace: 'affiliate' as const, title: 'affiliate.nav.notifications' },
  },
  {
    path: '/app/landlord/analytics/:section?',
    name: 'landlord-analytics',
    component: () => import('./pages/LandlordAnalyticsPage.vue'),
    meta: {
      ...base,
      workspace: 'landlord' as const,
      viewParam: true,
      title: 'landlord.nav.analytics',
    },
  },
  {
    path: '/app/landlord/notifications',
    name: 'landlord-notifications',
    component: () => import('~/modules/notifications/pages/NotificationsPage.vue'),
    meta: { ...base, workspace: 'landlord' as const, title: 'landlord.nav.notifications' },
  },
  {
    // 2026-10-01. The administrators' own centre: anomalies, critical data problems and the
    // queues waiting on them. Reached from the bell and from Account -> Notifications.
    path: '/app/admin/notifications',
    name: 'admin-notifications',
    component: () => import('~/modules/notifications/pages/NotificationsPage.vue'),
    meta: {
      ...base,
      workspace: 'admin' as const,
      capability: CAPABILITIES.adminAccess,
      title: 'landlord.nav.notifications',
    },
  },
  {
    path: '/app/admin/analytics/:section?',
    name: 'admin-analytics',
    component: () => import('./pages/AdminAnalyticsPage.vue'),
    meta: {
      ...base,
      workspace: 'admin' as const,
      viewParam: true,
      capability: CAPABILITIES.adminAccess,
      title: 'admin.nav.analytics',
    },
  },
]
